import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Info as InfoIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Matches SUPPLIER_TYPES in BankImportInterface.jsx / SupplierCard.jsx.
const SUPPLIER_TYPES = ['Supplies', 'Services', 'Products', 'Payroll'];

// Matches SUPPLIER_CATEGORIES — same list as BankImportInterface.jsx.
// Patch 80: these categories never have a bill.
const NO_BILL_CATEGORIES = ['Internal Transfer', 'Personal', 'Taxation', 'Loan Repayment', 'Bank Charges & Interest', 'Payroll', 'PAYE & NI'];
const categories = [
  'Accountancy & Legal',
  'Bank Charges & Interest',
  'Card Machine Fees',
  'Client Comfort',
  'Communications',
  'Electric',
  'Furniture & Equipment',
  'Gas',
  'Insurance',
  'Internal Transfer',
  'IT & Software',
  'Leasing and Finance',
  'Licences',
  'Loan Repayment',
  'Maintenance & Repairs',
  'Marketing & Advertising',
  'Office Supplies',
  'Payroll',
  'PAYE & NI',
  'Personal',
  'Postage and Courier',
  'Rates',
  'Rent',
  'Staff Welfare',
  'Stock',
  'Subscriptions',
  'Sundry',
  'Taxation',
  'Training',
  'Utilities',
  'Waste Disposal',
  'Water'
];

// Same trimming logic as BankImportInterface.jsx — cuts a raw bank
// description at the first run of 6+ digits so auto-generated supplier
// names don't inherit account numbers / reference strings.
const deriveDefaultSupplierName = (description) => {
  if (!description) return '';
  const cutIndex = description.search(/\s\d{6,}/);
  const cleaned = cutIndex > -1 ? description.slice(0, cutIndex) : description;
  // patch119: drop card suffix, dates, SP/SQSP prefixes and reference numbers from bank wording
  const tidy = cleaned
    .replace(/^(?:SQSP\*|SP\s+|PP\*)\s*/i, '')
    .replace(/\s+CD\s+\d{4}\b.*$/i, '')
    .replace(/\s*#\d+.*$/, '')
    .replace(/\d{6,}.*$/, '')
    .trim();
  return tidy || cleaned.trim();
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-GB');

/**
 * Standalone view of every unmatched bank transaction on the account,
 * independent of which import batch (or CSV upload) originally brought
 * it in. BankImportInterface's review screen only ever shows rows tied
 * to the import_batch_id from the CSV just uploaded — once that session
 * ends, any row left uncategorized or without a bill (e.g. because the
 * supplier didn't exist yet) becomes invisible in that flow. This page
 * is the recovery path: it queries unmatched transactions with no batch
 * filter, so nothing gets permanently stranded.
 *
 * REQUIRES a backend change: GET /api/bills/bank/transactions/unmatched
 * must treat a missing/omitted import_batch_id as "all unmatched
 * transactions for this account" rather than returning nothing. If the
 * route currently requires the param, add an account-scoped branch when
 * it's absent instead of filtering by batch.
 */
const UnmatchedTransactions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');

      // Deliberately no import_batch_id — this is the whole point of
      // this page. include_categorized=true is required so transactions
      // that already have a confirmed_category (like 1090) but never got
      // a bill created aren't silently excluded by the backend's
      // confirmed_category IS NULL filter. See backend patch notes.
      const params = { include_categorized: true };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const txnResponse = await axios.get('/api/bills/bank/transactions/unmatched', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      const unmatchedTxns = txnResponse.data.transactions || [];

      const withDraftState = unmatchedTxns.map((txn) => ({
        ...txn,
        selectedCategory: txn.confirmed_category || null,
        learnCategory: false,
        categorySaving: false,
        categorySaved: !!txn.confirmed_category,
        // Already-categorized rows are here because a bill was never
        // created for them (the categorize-then-stall gap that stranded
        // transaction 1090) — open the "create a bill" panel by default
        // so the next required step is immediately visible rather than
        // making the user rediscover it.
        isNewSupplierBill: txn.bill_required === true,
        billSupplierName: deriveDefaultSupplierName(txn.description),
        billSupplierType: 'Supplies',
        creatingBill: false,
        supplierMatch: null,
        supplierMatchChecking: false,
        supplierMatchDecision: null
      }));

      setTransactions(withDraftState);

      // Rows that arrived already categorized had their supplier-bill
      // panel auto-opened above — kick off the fuzzy-match check for
      // each so the propose/confirm UI is ready immediately rather than
      // waiting for the user to touch the field first.
      withDraftState
        .filter((t) => t.confirmed_category)
        .forEach((t) => checkSupplierMatch(t.id, t.billSupplierName));
    } catch (err) {
      setError('Failed to load unmatched transactions: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleCategoryChange = async (transactionId, category, learnOverride, billWantedOverride) => {
    setTransactions((prev) =>
      prev.map((t) =>
        t.id === transactionId
          ? { ...t, selectedCategory: category, categorySaving: true, categorySaved: false, isNewSupplierBill: NO_BILL_CATEGORIES.includes(category) ? false : t.isNewSupplierBill }
          : t
      )
    );

    const txn = transactions.find((t) => t.id === transactionId);
    const learn = learnOverride !== undefined ? learnOverride : txn ? txn.learnCategory : false;
    const billWanted = billWantedOverride !== undefined ? billWantedOverride : txn ? !!txn.isNewSupplierBill : false;

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/bills/bank/transactions/categorize',
        {
          transaction_id: transactionId,
          category_name: category,
          learn_category: learn,
          // NEW: tells the backend whether a bill is currently wanted for
          // this transaction, so bill_required gets set correctly and
          // Personal/Payroll rows with no bill intended stop reappearing
          // on this page after refresh.
          create_bill_wanted: billWanted
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTransactions((prev) =>
        prev.map((t) => (t.id === transactionId ? { ...t, categorySaving: false, categorySaved: true } : t))
      );
    } catch (err) {
      setTransactions((prev) =>
        prev.map((t) => (t.id === transactionId ? { ...t, categorySaving: false, categorySaved: false } : t))
      );
      setError(
        `Failed to save category for "${txn ? txn.description : 'transaction'}": ` +
          (err.response?.data?.error || err.message)
      );
    }
  };

  const handleLearnCategory = (transactionId, learn) => {
    setTransactions((prev) => prev.map((t) => (t.id === transactionId ? { ...t, learnCategory: learn } : t)));
    const txn = transactions.find((t) => t.id === transactionId);
    if (txn && txn.selectedCategory) {
      handleCategoryChange(transactionId, txn.selectedCategory, learn);
    }
  };

  const checkSupplierMatch = async (transactionId, nameOverride) => {
    const txn = transactions.find((t) => t.id === transactionId);
    const nameToCheck = (nameOverride !== undefined ? nameOverride : txn?.billSupplierName || '').trim();

    if (!nameToCheck) {
      setTransactions((prev) =>
        prev.map((t) => (t.id === transactionId ? { ...t, supplierMatch: null, supplierMatchDecision: null } : t))
      );
      return;
    }

    setTransactions((prev) =>
      prev.map((t) =>
        t.id === transactionId ? { ...t, supplierMatchChecking: true, supplierMatchDecision: null } : t
      )
    );

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/bills/bank/supplier-match-suggestion', {
        params: { description: nameToCheck },
        headers: { Authorization: `Bearer ${token}` }
      });

      const suggestion = response.data;
      const hasSuggestion = suggestion && suggestion.match_type;

      setTransactions((prev) =>
        prev.map((t) =>
          t.id === transactionId
            ? {
                ...t,
                supplierMatch: hasSuggestion ? suggestion : null,
                supplierMatchChecking: false,
                supplierMatchDecision: hasSuggestion ? null : 'confirmed_new'
              }
            : t
        )
      );
    } catch (err) {
      setTransactions((prev) =>
        prev.map((t) =>
          t.id === transactionId
            ? { ...t, supplierMatch: null, supplierMatchChecking: false, supplierMatchDecision: 'confirmed_new' }
            : t
        )
      );
    }
  };

  const acceptSuggestedSupplier = (transactionId) => {
    setTransactions((prev) =>
      prev.map((t) => {
        if (t.id !== transactionId || !t.supplierMatch) return t;
        return { ...t, billSupplierName: t.supplierMatch.supplier_name, supplierMatchDecision: 'use_existing' };
      })
    );
  };

  const declineSuggestedSupplier = (transactionId) => {
    setTransactions((prev) =>
      prev.map((t) => (t.id === transactionId ? { ...t, supplierMatchDecision: 'confirmed_new' } : t))
    );
  };

  const dismissNoBill = async (transactionId) => {
    const txn = transactions.find((t) => t.id === transactionId);
    if (!txn || !txn.selectedCategory) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/bills/bank/transactions/categorize',
        {
          transaction_id: transactionId,
          category_name: txn.selectedCategory,
          learn_category: false,
          create_bill_wanted: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTransactions((prev) => prev.filter((x) => x.id !== transactionId));
    } catch (err) {
      setError(
        `Failed to save "${txn.description}": ` + (err.response?.data?.error || err.message)
      );
    }
  };

  const toggleNewSupplierBillMode = (transactionId, isBill) => {
    setTransactions((prev) => prev.map((t) => (t.id === transactionId ? { ...t, isNewSupplierBill: isBill } : t)));
    if (isBill) {
      checkSupplierMatch(transactionId);
    }
    // Ticking/unticking after a category is already saved changes what
    // bill_required should be — re-send the categorize call so the
    // backend flag stays in sync with the current checkbox state rather
    // than reflecting whatever it was when the category was first saved.
    const txn = transactions.find((t) => t.id === transactionId);
    if (txn && txn.selectedCategory) {
      handleCategoryChange(transactionId, txn.selectedCategory, txn.learnCategory, isBill);
    }
  };

  const updateBillDraftField = (transactionId, field, value) => {
    setTransactions((prev) => prev.map((t) => (t.id === transactionId ? { ...t, [field]: value } : t)));
    if (field === 'billSupplierName') {
      setTransactions((prev) =>
        prev.map((t) => (t.id === transactionId ? { ...t, supplierMatch: null, supplierMatchDecision: null } : t))
      );
    }
  };

  const createBillFromTransaction = async (transactionId) => {
    const txn = transactions.find((t) => t.id === transactionId);
    if (!txn || !txn.billSupplierName || !txn.billSupplierName.trim()) {
      setError('Please enter a supplier name before creating a bill.');
      return;
    }

    setTransactions((prev) => prev.map((t) => (t.id === transactionId ? { ...t, creatingBill: true } : t)));

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/bills/bank/create-bill-from-transaction',
        {
          transaction_id: transactionId,
          supplier_name: txn.billSupplierName.trim(),
          supplier_type: txn.billSupplierType || 'Supplies',
          category_name: txn.selectedCategory || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Resolved — drop it from the stranded-transactions list.
      setTransactions((prev) => prev.filter((t) => t.id !== transactionId));
    } catch (err) {
      setTransactions((prev) => prev.map((t) => (t.id === transactionId ? { ...t, creatingBill: false } : t)));
      setError('Failed to create bill: ' + (err.response?.data?.error || err.message));
    }
  };

  const filteredTransactions = transactions.filter((t) => {
    if (!search.trim()) return true;
    return t.description.toLowerCase().includes(search.trim().toLowerCase());
  });

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Unmatched Transactions
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Every bank transaction that still needs a category or a bill, from any import — not just your
            most recent upload.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={loadTransactions}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card sx={{ mt: 3, mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label="Search description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <Chip label={filteredTransactions.length} color="info" sx={{ width: '100%' }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!loading && filteredTransactions.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Why this page matters
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Payments with a category are already counted in your reports. Payments with no category are not counted until you choose one.
              Create a bill only if you have an invoice or receipt for the payment, so the supplier and VAT details are recorded.
              If you do not have one, click "No bill needed". For VAT-registered accounts, no bill means no VAT is claimed on that payment.
              Wages, transfers, tax payments, credit card repayments (choose Loan Repayment) and bank charges never need a bill. Credit card statements cannot be imported, so card purchases do not appear here, only the repayment does.
            </Typography>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && filteredTransactions.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <InfoIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Nothing waiting here
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {transactions.length === 0
                ? 'Every bank transaction on this account has a category or a bill.'
                : 'No transactions match your search.'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading &&
        filteredTransactions.map((txn) => (
          <Paper key={txn.id} sx={{ p: 2, mb: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={2}>
                <Typography variant="body2" fontWeight="bold">
                  {formatDate(txn.transaction_date)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>{txn.description}</Typography>
                  {txn.confirmed_category && (
                    <Chip label="Bill optional" color="warning" size="small" variant="outlined" title="Already counted in your reports. Create a bill only if you have an invoice or receipt for this payment. Without a bill, no VAT is claimed on it." />
                  )}
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {txn.reference}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="h6" align="right">
                  {formatCurrency(txn.amount)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={txn.selectedCategory || ''}
                    label="Category"
                    onChange={(e) => handleCategoryChange(txn.id, e.target.value)}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {txn.categorySaving && (
                  <Typography variant="caption" color="text.secondary">
                    Saving…
                  </Typography>
                )}
                {!txn.categorySaving && txn.categorySaved && (
                  <Typography variant="caption" color="success.main">
                    ✓ Saved
                  </Typography>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={txn.learnCategory}
                      onChange={(e) => handleLearnCategory(txn.id, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="caption" title="Matches this exact payee text next time. Only useful where the text is identical every time, not where it includes a changing code.">Remember "{txn.description}" = this category</Typography>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  sx={{ display: NO_BILL_CATEGORIES.includes(txn.selectedCategory) ? 'none' : undefined }}
                  control={
                    <Checkbox
                      size="small"
                      checked={txn.isNewSupplierBill}
                      onChange={(e) => toggleNewSupplierBillMode(txn.id, e.target.checked)}
                    />
                  }
                  label={<Box><Typography variant="body2">Create a bill for this transaction</Typography><Typography variant="caption" color="textSecondary">Tick only if you have an invoice or receipt for this payment.</Typography></Box>}
                />

                {!NO_BILL_CATEGORIES.includes(txn.selectedCategory) && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ ml: 2 }}
                    onClick={() => dismissNoBill(txn.id)}
                    title="No VAT is claimed on this payment. It still counts in your VAT return purchases figure if you are on the standard scheme."
                  >
                    No bill needed
                  </Button>
                )}

                {txn.isNewSupplierBill && (
                  <Box sx={{ mt: 1, pl: 4 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Supplier Name"
                          value={txn.billSupplierName}
                          onChange={(e) => updateBillDraftField(txn.id, 'billSupplierName', e.target.value)}
                          onBlur={() => checkSupplierMatch(txn.id)}
                          helperText="If SalonLogic suggests an existing supplier, press Yes. Only type a new name for a business you have never paid before."
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Supplier Type</InputLabel>
                          <Select
                            value={txn.billSupplierType}
                            label="Supplier Type"
                            onChange={(e) => updateBillDraftField(txn.id, 'billSupplierType', e.target.value)}
                          >
                            {SUPPLIER_TYPES.map((t) => (
                              <MenuItem key={t} value={t}>
                                {t}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          disabled={
                            txn.creatingBill ||
                            !txn.billSupplierName.trim() ||
                            txn.supplierMatchChecking ||
                            (txn.supplierMatch && !txn.supplierMatchDecision)
                          }
                          onClick={() => createBillFromTransaction(txn.id)}
                          startIcon={txn.creatingBill ? <CircularProgress size={16} /> : null}
                        >
                          {txn.creatingBill ? 'Creating…' : 'Create Bill'}
                        </Button>
                      </Grid>
                    </Grid>

                    {txn.supplierMatchChecking && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="text.secondary">
                          Checking existing suppliers…
                        </Typography>
                      </Box>
                    )}

                    {txn.supplierMatch && !txn.supplierMatchDecision && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2" gutterBottom>
                          {txn.supplierMatch.match_type === 'alias' ? (
                            <>
                              This looks like <strong>{txn.supplierMatch.supplier_name}</strong> (known alias) —
                              use this supplier?
                            </>
                          ) : (
                            <>
                              This looks like <strong>{txn.supplierMatch.supplier_name}</strong> (
                              {Math.round(txn.supplierMatch.similarity * 100)}% match) — use this supplier?
                            </>
                          )}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => acceptSuggestedSupplier(txn.id)}
                          >
                            Yes, use this supplier
                          </Button>
                          <Button variant="outlined" size="small" onClick={() => declineSuggestedSupplier(txn.id)}>
                            No, it's different
                          </Button>
                        </Box>
                      </Alert>
                    )}

                    {txn.supplierMatchDecision === 'use_existing' && (
                      <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                        ✓ Will attach this bill to the existing supplier "{txn.billSupplierName}".
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      VAT is calculated at 20% (one-sixth of the payment) for suppliers marked VAT registered on the Supplier Card. Other suppliers get no VAT. This page cannot enter a different rate. If your invoice shows 5%, nil VAT or a mix, choose No bill needed and ask your accountant. Confirm the supplier's status on the Supplier Card afterwards. 
                      
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        ))}
    </Box>
  );
};

export default UnmatchedTransactions;